/**
 * Comment-aware scanning primitives shared by every module that reads these
 * emails as raw HTML.
 *
 * WHY THIS FILE EXISTS: three separate comment-blindness bugs were found in
 * splitTemplate.js during review, each one because some new scan reached for a
 * plain regex instead of the comment-aware path. Marketing email markup is full
 * of MSO conditionals (<!--[if mso]><table>...<![endif]-->), so a scanner that
 * does not discard comments will find <table>, </body> and closing tags that do
 * not exist in the rendered document. Every consumer must go through
 * scanOutsideComments, and there must be exactly one of it — hence a shared
 * module rather than a copy per file.
 *
 * Pure: no Mongo, no network, no clock.
 */

/**
 * Iterate real (non-comment) matches of `innerPattern` in `text`. Comments —
 * including MSO conditionals like <!--[if mso]>...<![endif]--> — are matched
 * and discarded as a single unit first, via alternation, so anything written
 * inside one (a stray </body>, a fake <table>, a mismatched closing tag) is
 * never mistaken for real markup.
 *
 * The alternation order matters: the comment branch is first, so a comment that
 * begins before a candidate match always swallows it. Putting the caller's
 * pattern first would let a <td> inside a comment win the race at that offset.
 *
 * `innerPattern` is a regex source string (no delimiters, no flags); its
 * capture groups are preserved on the returned match objects, offset by one
 * because this function contributes no groups of its own.
 */
function scanOutsideComments(text, innerPattern) {
  const token = new RegExp(`<!--[\\s\\S]*?-->|(?:${innerPattern})`, 'gi');
  const matches = [];
  let match = token.exec(text);
  while (match !== null) {
    if (match[0].slice(0, 4) !== '<!--') matches.push(match);
    match = token.exec(text);
  }
  return matches;
}

// Elements with no closing tag. A void element (or an explicitly self-closed
// <x/>) never pushes onto the balance stack below.
const VOID_ELEMENTS = new Set([
  'area', 'base', 'br', 'col', 'embed', 'hr', 'img', 'input', 'link', 'meta',
  'param', 'source', 'track', 'wbr',
]);

/**
 * Conservative structural balance check for a fragment taken in isolation.
 *
 * In splitTemplate.js this is the direct test for the failure mode the
 * round-trip assertion cannot see: header+content+footer always concatenate to
 * the same bytes regardless of where the cut falls, so a wrong-but-balanced cut
 * sails through reassemble() and normalizeHtml() undetected. A region that only
 * balances once glued to its neighbours is a cut through the middle of an
 * element, and must be caught here instead.
 *
 * In editableCells.js it guards the other direction: replacement copy that
 * closes a tag it never opened would tear the surrounding table apart once
 * spliced in, and the splice itself cannot notice.
 *
 * Comments (including MSO conditionals) are skipped via scanOutsideComments.
 * Void elements and self-closed tags never push onto the stack. Deliberately
 * NOT lenient about legal-but-unclosed HTML (<p>, <li> left open by implied
 * closing) — that reads as unbalanced and refuses, which is the correct,
 * intended outcome here: refuse rather than guess.
 */
function isTagBalanced(fragment) {
  const matches = scanOutsideComments(fragment, '<(/?)([a-zA-Z][a-zA-Z0-9-]*)\\b[^>]*?(/?)>');
  const stack = [];

  for (const match of matches) {
    const isClose = match[1] === '/';
    const name = match[2].toLowerCase();
    const isSelfClosing = match[3] === '/';

    if (!isClose && (isSelfClosing || VOID_ELEMENTS.has(name))) {
      continue;
    }

    if (isClose) {
      if (stack.length === 0 || stack[stack.length - 1] !== name) return false;
      stack.pop();
    } else {
      stack.push(name);
    }
  }

  return stack.length === 0;
}

module.exports = { scanOutsideComments, isTagBalanced, VOID_ELEMENTS };
